import React, {useEffect, useState} from "react";
import {
    BoxIsolatedContent,
    ContentTop,
    ContentXsTop,
    FlexColumn,
    FlexColumnCentered,
    FlexRow,
    FormFooter,
    FormGoBack,
    IconContainer,
} from "../../../../components/ui/Content";
import {Body1, Body1Pointer, Body1PointerLink, Body2, Caption2, Title} from "../../../../components/Font";
import {ButtonPrimary} from "../../../../components/ui/Button";
import {useTheme} from "styled-components";
import {useTranslation} from "react-i18next";
import {InputAutoCompleteEnum, InputElement, InputTypeEnum} from "../../../../ui/Input/Input";
import {
    Authorization,
    BindConnection,
    CheckConnections,
    CheckConnectionsAdmin,
    cleanError,
    ContinueConnection, forceSetToUserChoose,
    hidePasswordExpiryWarning,
    resetConnectionState,
    setAccountToConnectId,
    setIsTryingAsAdmin,
    SignIn,
    SkipConnection
} from "../../slice";
import {useAppDispatch, useAppSelector} from "../../../../app/hooks";
import {LoaderContainer, LoaderEnum, MiniLoader} from "../../../../ui/Loader/MiniLoader";
import {ForgotPassword, LoginInputContainer, RegisterInputContainer} from "../../../../components/ui/InputSearch";
import {CircleQuestion, GoBack, GreenCircleCheck, GreenConnection} from "../../../../icon/Icon";
import {LanguageSelector} from "../../LanguageSelector";
import {useWindowResize} from "../../../../hooks/useWindowSize";
import styled from "styled-components/macro";
import {useLocation, useNavigate} from "react-router-dom";
import {RecoveryAccount} from "../../RecoverySteps/RecoveryAccount";
import {trackHit} from "../../../../utils/analytics";
import {PasswordExpiryWarning} from "../PasswordExpiryWarning";

export const LogInForm = () => {

    const Theme = useTheme()
    const {t} = useTranslation();
    const [hint, setHint] = useState({
        show: false,
        coords: {
            top: 0,
            left: 0
        }
    })
    const [user, setUser] = useState({
        login: '',
        password: ''
    })

    const [connectPassword, setConnectPassword] = useState('')
    const [confirmed, setConfirmed] = useState(false)
    const [connectWarningShow, setConnectWarningShow] = useState(false)

    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const {search} = useLocation()
    const {
        error,
        isAuthFetching,
        connection,
        isFetching,
        token,
        needAuth,
        isTryingAsAdmin,
        unbindingsAccounts,
        passwordExpiryWarning,
    } = useAppSelector(state => state.auth)

    React.useEffect(() => {
        trackHit();
    }, [search]);

    const {
        isBinding,
        token: connectToken,
        data: connections,
        accountToConnectId,
        connectedAccountId,
        accountSubmitted,
        canLogIn,
    } = connection

    const connectedAccount = connections.find(i => i.common_name === connectedAccountId)
    const accountToConnect = connections.find(i => i.common_name === accountToConnectId)

    const {isDesktop} = useWindowResize()

    useEffect(() => {
        const input = document.querySelector('.loginNameInput') as HTMLInputElement
        input?.focus()
    }, [])

    useEffect(() => {
        setConnectPassword('')
    }, [confirmed])

    useEffect(() => {
        // Не выполняем автоматическую авторизацию, если показано предупреждение о пароле
        if (connectToken && canLogIn && !token && !passwordExpiryWarning.show) {
            submitAdmin()
        }
    }, [connectToken, canLogIn, passwordExpiryWarning.show])

    useEffect(() => {
        // Не выполняем привязку аккаунта, если показано предупреждение о пароле
        if (needAuth === false && !passwordExpiryWarning.show) {
            bindConnection()
        }
    }, [needAuth, passwordExpiryWarning.show])

    const submit = () => {
        dispatch(CheckConnections({
            username: user.login,
            password: user.password,
            ad_person_id: null
        }))
        localStorage.setItem('userLogin', user.login)
    }

    const [selectedUser, setSelectedUser] = useState(localStorage.getItem('selectedUser'))
    useEffect(() => {
        if (selectedUser) {
            dispatch(CheckConnections({
                username: user.login,
                password: user.password,
                ad_person_id: Number(selectedUser)
            }))
        }
    }, [selectedUser])
    const bindConnection = () => {
        if (accountToConnect && connectToken) {
            dispatch(BindConnection({
                data: {
                    common_name: accountToConnect.common_name,
                    password: connectPassword || null,
                    ad_id: accountToConnect.ad_id
                },
                token: connectToken
            })).then(() => {
                setConnectWarningShow(false)
            })
        }
    }

    const submitAuth = (connectToken: string) => {
        const urlSearch = new URLSearchParams(window.location.search)
        const client_id = urlSearch.get('client_id')
        if (client_id) {
            dispatch(Authorization(connectToken))
        } else {
            dispatch(SignIn(connectToken))
        }
    }

    const submitAdmin = () => {
        if (isTryingAsAdmin){
            dispatch(CheckConnectionsAdmin({
                username: user.login,
                password: user.password
            }))
        }else{
            dispatch(forceSetToUserChoose())
        }
    }

    const onSubmitButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        if (e.ctrlKey || e.metaKey) {
            dispatch(setIsTryingAsAdmin(true))
        } else {
            dispatch(setIsTryingAsAdmin(false))
        }
        submit()
    }

    const [longPress, setLongPress] = useState(false);
    const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);

    const onTouchStart = () => {
        const id = setTimeout(() => {
            dispatch(setIsTryingAsAdmin(true));
            setLongPress(true);
            submit()
        }, 5000); // мс
        setTimeoutId(id);
    };
    const onTouchEnd = () => {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        if (!longPress) {
            dispatch(setIsTryingAsAdmin(false));
            submit()
        }
        setLongPress(false);
    }

    if (connections.length > 0) {

        if (accountSubmitted) {

            return <><FlexColumnCentered className={'box-isolated'}>
                <Title>{t('auth.accountsBinding')}</Title>
                <BoxIsolatedContent>
                    <ContentXsTop>
                        <Body1 color={Theme.color.secondary}>
                            {`Мы связали аккаунт ${connectedAccount?.common_name} (${connectedAccount?.email}) с данной учетной записью`}
                        </Body1>
                    </ContentXsTop>

                    {isBinding ? <ButtonPrimary>
                            <LoaderContainer>
                                <MiniLoader color={Theme.color.primaryBackground} size={LoaderEnum.small}/>
                            </LoaderContainer>
                        </ButtonPrimary>
                        : <ButtonPrimary onClick={() => {
                            if (connectToken) {
                                dispatch(ContinueConnection(connectToken)).then(() => {
                                    submitAuth(connectToken)
                                })
                            }
                        }
                        }>
                            {t('auth.logIn')}
                        </ButtonPrimary>}
                </BoxIsolatedContent>
                <LanguageSelector/>
            </FlexColumnCentered>
            </>
        }

        if (confirmed) {

            if (connectWarningShow) {
                return <><FlexColumnCentered className={'box-isolated'}>
                    <Title>{t('auth.attention')}!</Title>
                    <BoxIsolatedContent>
                        <ContentXsTop>
                            <Body1 color={Theme.color.secondary}>
                                {t('auth.importedAccountsMessage')}
                            </Body1>
                        </ContentXsTop>

                        {isBinding ? <ButtonPrimary>
                                <LoaderContainer>
                                    <MiniLoader color={Theme.color.primaryBackground} size={LoaderEnum.small}/>
                                </LoaderContainer>
                            </ButtonPrimary>
                            :
                            <ButtonPrimary disabled={needAuth !== false && connectPassword.length < 3} onClick={() => {
                                if (connectPassword.length > 3) {
                                    bindConnection()
                                } else {
                                    if (needAuth === false) {
                                        bindConnection()
                                    }
                                }
                            }
                            }>
                                {t('auth.bind')}
                            </ButtonPrimary>}
                    </BoxIsolatedContent>
                    <ContentTop>
                        <Body1PointerLink onClick={() => {
                            setConnectWarningShow(false)
                        }}>
                            {t('auth.cancel')}
                        </Body1PointerLink>
                    </ContentTop>
                    <LanguageSelector/>
                </FlexColumnCentered>
                </>
            }

            return <><FlexColumnCentered className={'box-isolated'}>
                <FormGoBack style={{left: 24, top: 34}} onClick={() => {
                    if (error['password']) {
                        dispatch(cleanError('password'))
                    }
                    setConfirmed(false)
                }}>
                    <GoBack/>
                </FormGoBack>
                <Title>{t('auth.accountBind')}</Title>
                <BoxIsolatedContent>
                    <ContentXsTop>
                        <Body1 color={Theme.color.secondary}>
                            {t('auth.confirmOperationEnterPassword')}
                        </Body1>
                    </ContentXsTop>
                    <ContentXsTop style={{width: '100%', textAlign: 'start', marginTop: 30}}>
                        <Caption2 color={Theme.color.secondary}>
                            {t('auth.logIn')}
                        </Caption2>
                        <Body1>
                            {accountToConnect?.common_name}
                        </Body1>
                    </ContentXsTop>
                    <ContentXsTop style={{width: '100%', textAlign: 'start'}}>
                        <Caption2 color={Theme.color.secondary}>
                            {t('auth.emailEnter')}
                        </Caption2>
                        <Body1>
                            {accountToConnect?.email}
                        </Body1>
                    </ContentXsTop>

                    {needAuth !== false && <RegisterInputContainer>
                        <InputElement
                            type={InputTypeEnum.Password}
                            error={error['password']}
                            placeholder={t('auth.passwordPlaceholder')}
                            value={connectPassword}
                            onChange={(value) => {
                                if (error['password']) {
                                    dispatch(cleanError('password'))
                                }
                                setConnectPassword(value)
                            }}
                            onKeyDown={(e) => {
                                if (error['password']) {
                                    dispatch(cleanError('password'))
                                }
                                if (e === 'Enter' && (connectPassword.length > 3)) {
                                    setConnectWarningShow(true)
                                }
                            }}
                            autoComplete={InputAutoCompleteEnum.NewPassword}
                        />
                    </RegisterInputContainer>}


                    <ButtonPrimary disabled={needAuth !== false && connectPassword.length < 4} onClick={() => {
                        setConnectWarningShow(true)
                    }}>
                        {t('auth.bind')}
                    </ButtonPrimary>
                </BoxIsolatedContent>
                <LanguageSelector/>
            </FlexColumnCentered>
            </>
        }

        return <><FlexColumnCentered className={'box-isolated'}>
            <Title>{t('auth.accountBind')}</Title>
            <BoxIsolatedContent>
                <ContentXsTop>
                    <Body1 color={Theme.color.secondary}>
                        {t('auth.selectPrimaryDomain')}
                    </Body1>
                </ContentXsTop>
                <ContentXsTop/>

                {connections.map((item, index) => {
                    return <FlexRow
                        className={'svg-min16'}
                        style={{
                            cursor: 'pointer',
                            width: '100%',
                            padding: '12px 0',
                            alignItems: 'center',
                            gap: '12px',
                            borderBottom: index !== (connections.length - 1)
                                ? '0.5px solid rgba(0, 0, 0, 0.12)'
                                : ''
                        }}
                        onClick={() => {
                            if (accountToConnectId === item.common_name) {
                                dispatch(setAccountToConnectId(null))
                            } else {
                                dispatch(setAccountToConnectId(item.common_name))
                            }
                        }} key={index}>
                        {accountToConnectId === item.common_name && <IconContainer>
                            <GreenCircleCheck/>
                        </IconContainer>}
                        <FlexColumn style={{
                            alignItems: 'start',
                            gap: '2px',
                            width: '100%',

                        }}>
                            <Body2>
                                {item.common_name}
                            </Body2>
                            <Caption2>
                                {item.email}
                            </Caption2>
                        </FlexColumn>
                        {connectedAccount?.common_name === item.common_name && <GreenConnection/>}
                    </FlexRow>
                })}

                {!isBinding && <ButtonPrimary disabled={!accountToConnect} onClick={() => {
                    if (accountToConnect) {
                        setConfirmed(true)
                    }
                }}>
                    {t('auth.bind')}
                </ButtonPrimary>}
            </BoxIsolatedContent>
            <ContentTop>
                <Body1PointerLink onClick={() => {
                    if (connectToken && !isBinding) {

                        dispatch(SkipConnection(connectToken)).then(() => {
                            dispatch(ContinueConnection(connectToken)).then(() => {
                                submitAdmin()
                            })
                        })
                    }
                }}>
                    {(isBinding || isFetching)
                        ? <MiniLoader color={Theme.color.indicator} size={LoaderEnum.small}/>
                        : t('auth.skipAndGoToProfile')}
                </Body1PointerLink>
            </ContentTop>
            <LanguageSelector/>
        </FlexColumnCentered>
        </>
    }

    // Обработчики для модального окна предупреждения о пароле
    const handleSkipPasswordWarning = () => {
        dispatch(hidePasswordExpiryWarning())
        // После скрытия модалки продолжаем нормальный флоу авторизации
        if (connectToken && canLogIn && !token) {
            if (isTryingAsAdmin) {
                submitAdmin()
            } else {
                dispatch(forceSetToUserChoose())
            }
        } else if (connectToken) {
            submitAuth(connectToken)
        } else if (needAuth === false) {
            bindConnection()
        }
    }

    const handleChangePassword = () => {
        dispatch(hidePasswordExpiryWarning())
        dispatch(resetConnectionState())
        // Очищаем localStorage от временных данных
        localStorage.removeItem('selectedUser')
        navigate(`/password/recovery${search}`)
    }

    // Показываем модальное окно предупреждения о пароле ПЕРЕД проверкой несвязанных аккаунтов
    if (passwordExpiryWarning.show && passwordExpiryWarning.daysLeft !== null) {
        return <PasswordExpiryWarning
            daysLeft={passwordExpiryWarning.daysLeft}
            onSkip={handleSkipPasswordWarning}
            onChangePassword={handleChangePassword}
        />
    }

    if(unbindingsAccounts && !selectedUser){
        return <RecoveryAccount setSelectedUserAuth={setSelectedUser} />
    }

    return <><FlexColumnCentered className={'box-isolated'}>
        <BoxIsolatedContent>

            <Title>{t('auth.logInAccount')}</Title>

            <LoginInputContainer>
                <InputElement
                    className={'loginNameInput'}
                    error={error['username']}
                    placeholder={t('auth.loginTitle')}
                    value={user.login}
                    onChange={(value) => {
                        if (error['username']) {
                            dispatch(cleanError('username'))
                        }
                        setUser({...user, login: value})
                    }}
                    disabled={isAuthFetching}
                />
                {isDesktop && <IconContainer
                    onMouseEnter={(e) => {
                        setHint({
                            show: true,
                            coords: {
                                top: e.currentTarget.getBoundingClientRect().top,
                                left: e.currentTarget.getBoundingClientRect().left
                            }
                        })
                    }}
                    onMouseLeave={() => {
                        setHint({
                            ...hint,
                            show: false
                        })
                    }}
                    style={{
                        position: 'absolute',
                        right: '15px',
                        top: '40px'
                    }}>
                    <CircleQuestion/>
                </IconContainer>}

            </LoginInputContainer>
            <Hint
                className={hint.show ? 'visibleHint' : 'invisibleDelay'}
                style={{
                    top: (hint.coords.top - 80) + 'px',
                    left: (hint.coords.left + 40) + 'px'
                }}>
                <Body1 style={{textAlign: 'start'}}>
                    {t('auth.loginCanBe')}
                </Body1>
                <ul>
                    <li>{t('auth.corporateMail')}</li>
                    <li>{t('auth.electronicMail')}</li>
                    <li>{t('auth.mobilePhone')}</li>
                </ul>
            </Hint>

            {!isDesktop && <Body1 style={{textAlign: 'start', margin: '12px 0 0'}}>
                {t('auth.loginHint')}
            </Body1>}

            <RegisterInputContainer>
                <InputElement
                    type={InputTypeEnum.Password}
                    error={error['password']}
                    placeholder={t('auth.passwordPlaceholder')}
                    value={user.password}
                    onChange={(value) => {
                        if (error['password']) {
                            dispatch(cleanError('password'))
                        }
                        setUser({...user, password: value})
                    }}
                    onKeyDown={(e) => {
                        if (error['password']) {
                            dispatch(cleanError('password'))
                        }
                        if (e === 'Enter' && (user.login.length > 1 && user.password.length > 1)) {
                            submit()
                        }
                    }}
                    disabled={isAuthFetching}
                    autoComplete={InputAutoCompleteEnum.CurrentPassword}
                />
            </RegisterInputContainer>

            <ForgotPassword>
                <Body1Pointer color={Theme.color.indicator} onClick={() => {
                    navigate(`/password/recovery${search}`)
                }}>
                    {t('auth.forgotPassword')}
                </Body1Pointer>
            </ForgotPassword>

            {(isAuthFetching || isFetching)
                ? <ButtonPrimary>
                    <LoaderContainer>
                        <MiniLoader color={Theme.color.primaryBackground} size={LoaderEnum.small}/>
                    </LoaderContainer>
                </ButtonPrimary>
                : <ButtonPrimary disabled={user.login.length < 1 || user.password.length < 1}
                    onClick={onSubmitButtonClick}
                    onTouchStart={onTouchStart}
                    onTouchEnd={onTouchEnd}
                >
                    {t('auth.logIn')}
                </ButtonPrimary>}
        </BoxIsolatedContent>
        <FormFooter style={{borderTop: Theme.border.default}}>
            <Body1PointerLink onClick={() => {
                navigate(`/sign-up${search}`)
            }}>
                {t('auth.createAccount')}
            </Body1PointerLink>
        </FormFooter>
        <LanguageSelector/>
    </FlexColumnCentered>
    </>
}

const Hint = styled.div`
  display: flex;
  position: fixed;
  padding: 24px;
  flex-direction: column;
  gap: 8px;
  border-radius: 9px;
  background: #FFFFFF;
  width: 214px;
  filter: drop-shadow(0px 0px 2px rgba(0, 0, 0, 0.08)) drop-shadow(0px 2px 24px rgba(0, 0, 0, 0.08));
  z-index: 100;

  ul {
    margin: 0;
    padding-left: 15px;

    li {
      text-align: start;
    }
  }

  :after {
    background: #FFFFFF;
    display: flex;
    content: '';
    width: 20px;
    height: 20px;
    border-radius: 4px;
    position: absolute;
    top: 50%;
    left: -15px;
    transform: rotate(45deg) translateY(-50%);
  }
`
